import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PublicLayout } from "@/components/Navbar";
import { Building2, CheckCircle2 } from "lucide-react";

export default function ApplyTrade() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", password: "", phone: "", vat_number: "", address: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/public/trade-application", form);
      setDone(true);
      toast.success("Application submitted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit");
    } finally { setLoading(false); }
  };

  if (done) return (
    <PublicLayout>
      <div className="mx-auto max-w-lg px-4 py-28 text-center">
        <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-400" />
        <h1 className="mt-6 font-serif-display text-3xl font-bold text-slate-50">Application received</h1>
        <p className="mt-3 text-sm text-[#94A3B8]">Thank you. Our trade team will review your application and email you once your account is approved. You can then sign in to access wholesale pricing and ordering.</p>
        <button onClick={() => navigate("/login")} data-testid="goto-login-btn" className="mc-btn-gold mt-8 rounded-full px-8 py-3">Go to Sign In</button>
      </div>
    </PublicLayout>
  );

  const field = (label, k, type = "text", required = true) => (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-200">{label}{required && <span className="text-[#D4AF37]"> *</span>}</label>
      <input data-testid={`apply-${k}`} type={type} required={required} value={form[k]} onChange={upd(k)}
        className="w-full rounded-xl border border-[#2A2E39] bg-[#1A1D24] px-4 py-3 text-sm text-slate-100 placeholder:text-[#64748B] focus:border-[#D4AF37]/60 focus:outline-none" />
    </div>
  );

  return (
    <PublicLayout>
      <div className="mx-auto max-w-2xl px-4 py-14 sm:px-8">
        <div className="flex items-center gap-3 text-[#D4AF37]"><Building2 className="h-6 w-6" /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Trade Account Application</span></div>
        <h1 className="mt-3 font-serif-display text-4xl font-bold text-slate-50">Apply for a <span className="mc-gold-gradient">Trade Account</span></h1>
        <p className="mt-3 text-sm text-[#94A3B8]">Unlock wholesale pricing and live, Sage-verified stock. Approved retailers can order directly on account.</p>

        <form onSubmit={submit} className="mt-10 grid gap-5 sm:grid-cols-2">
          {field("Company Name", "company_name")}
          {field("Contact Name", "contact_name")}
          {field("Email", "email", "email")}
          {field("Password", "password", "password")}
          {field("Phone", "phone", "text", false)}
          {field("VAT Number", "vat_number", "text", false)}
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-medium text-slate-200">Business / Delivery Address<span className="text-[#D4AF37]"> *</span></label>
            <textarea data-testid="apply-address" rows={2} required value={form.address} onChange={upd("address")}
              placeholder="Street, town, county, Eircode"
              className="w-full rounded-xl border border-[#2A2E39] bg-[#1A1D24] px-4 py-3 text-sm text-slate-100 placeholder:text-[#64748B] focus:border-[#D4AF37]/60 focus:outline-none" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-medium text-slate-200">Tell us about your business</label>
            <textarea data-testid="apply-message" rows={3} value={form.message} onChange={upd("message")}
              className="w-full rounded-xl border border-[#2A2E39] bg-[#1A1D24] px-4 py-3 text-sm text-slate-100 placeholder:text-[#64748B] focus:border-[#D4AF37]/60 focus:outline-none" />
          </div>
          <div className="sm:col-span-2">
            <button disabled={loading} data-testid="apply-submit-btn" className="mc-btn-gold w-full rounded-full px-8 py-3.5 text-base disabled:opacity-60">
              {loading ? "Submitting…" : "Submit Application"}
            </button>
          </div>
        </form>
      </div>
    </PublicLayout>
  );
}
